import React, { useEffect, useMemo, useState } from "react";
import { X, ShieldCheck, MessageCircle, CheckCircle2, AlertCircle, Trash2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useStock } from "@/context/StockContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import PolicyCard from "@/components/store/PolicyCard";
import { buildWhatsAppUrl } from "@/data/translations";
import { formatPrice, localized, localizedColor } from "@/data/products";
import { resolveAssetUrl } from "@/lib/assetUrl";
import { trackEvent } from "@/lib/analytics";

import { LOGO_LIGHT } from "@/data/branding";

const LOGO_URL = LOGO_LIGHT;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback list — used only if the backend is unreachable. The canonical
// source is `GET /api/content?platform=web` → store_info.payment_methods,
// which the admin toggles from /admin → Payments.
const FALLBACK_PAYMENT_METHODS = [
  { id: "omt", labelKey: "omt", subKey: "omtSub", enabled: true },
  { id: "whish", labelKey: "whish", subKey: "whishSub", enabled: true },
  { id: "cod", labelKey: "cod", subKey: "codSub", enabled: true },
];
const LABEL_KEY = { omt: "omt", whish: "whish", cod: "cod" };
const SUB_KEY = { omt: "omtSub", whish: "whishSub", cod: "codSub" };

const PHONE_REGEX = /^[\d\s+()-]{6,}$/;

// Build the stable cart-line key used by the cart context so we can match
// backend `unavailable` entries (product_id + size + color) back to local
// lines for a targeted remove.
const matchKey = (productId, size, color) =>
  `${productId}::${size || ""}::${color || ""}`;

export default function CheckoutModal({ open, onClose }) {
  const { t, lang, isRTL } = useLanguage();
  const S = t.store;
  const C = S.checkout;
  const { detailedItems, subtotal, shippingFee, total, clearCart, closeCart, removeFromCart } = useCart();
  const { refreshStock } = useStock();
  const { customer } = useCustomerAuth();

  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    address: "",
    city: "",
    notes: "",
    paymentMethod: "omt",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { order_no: "..." }
  const [submitError, setSubmitError] = useState("");
  // Set of cart-line keys currently flagged unavailable by the backend.
  const [unavailableKeys, setUnavailableKeys] = useState(new Set());

  // Enabled payment methods, sourced from /api/content (single source of
  // truth — same data the admin toggles inside /admin → Payments).
  // We refetch every time the modal opens so a toggle in the admin tab
  // is reflected the moment the customer revisits checkout.
  const [paymentMethods, setPaymentMethods] = useState(FALLBACK_PAYMENT_METHODS);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    fetch(`${BACKEND_URL}/api/content?platform=web`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!alive || !data) return;
        const remote = data?.store_info?.payment_methods;
        if (Array.isArray(remote) && remote.length > 0) {
          // Only show the ones the admin marked enabled.
          const enabled = remote
            .filter((m) => m && m.enabled !== false)
            .map((m) => ({
              id: m.id,
              labelKey: LABEL_KEY[m.id] || m.id,
              subKey: SUB_KEY[m.id] || `${m.id}Sub`,
              enabled: true,
            }));
          if (enabled.length > 0) setPaymentMethods(enabled);
        }
      })
      .catch(() => { /* fallback list already in place */ });
    return () => { alive = false; };
  }, [open]);

  // If the currently-selected method gets disabled remotely, switch to the
  // first enabled one so the customer can never submit a disabled method.
  useEffect(() => {
    if (!paymentMethods.some((m) => m.id === form.paymentMethod) && paymentMethods.length > 0) {
      setForm((p) => ({ ...p, paymentMethod: paymentMethods[0].id }));
    }
  }, [paymentMethods, form.paymentMethod]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setErrors({});
      setSubmitting(false);
      setSubmitError("");
      setUnavailableKeys(new Set());
    }
  }, [open]);

  useEffect(() => {
    if (!open || !customer) return;
    setForm((prev) => ({
      ...prev,
      fullName: prev.fullName || customer.full_name || "",
      phone: prev.phone || customer.phone || "",
      address: prev.address || customer.address || "",
      city: prev.city || customer.city || "",
      notes: prev.notes || customer.delivery_notes || "",
    }));
  }, [customer, open]);

  // Clear the unavailable flag for a line as soon as it's removed from the
  // cart so the message disappears once the customer takes action.
  useEffect(() => {
    if (unavailableKeys.size === 0) return;
    const stillThere = new Set();
    detailedItems.forEach((i) => {
      if (unavailableKeys.has(i.key)) stillThere.add(i.key);
    });
    if (stillThere.size !== unavailableKeys.size) {
      setUnavailableKeys(stillThere);
      if (stillThere.size === 0) setSubmitError("");
    }
  }, [detailedItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const itemsCount = useMemo(
    () => detailedItems.reduce((a, i) => a + i.qty, 0),
    [detailedItems]
  );

  const handleChange = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
    if (errors[field]) {
      setErrors((p) => ({ ...p, [field]: undefined }));
    }
  };

  const validate = () => {
    const next = {};
    if (!form.fullName.trim()) next.fullName = C.requiredField;
    if (!form.phone.trim()) next.phone = C.requiredField;
    else if (!PHONE_REGEX.test(form.phone.trim())) next.phone = C.invalidPhone;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) return;
    if (detailedItems.length === 0) return;

    setSubmitting(true);
    try {
      trackEvent("checkout_submit_attempt", {
        routeName: "checkout_modal",
        metadata: {
          items_count: detailedItems.length,
          subtotal,
          shipping_fee: shippingFee,
          total,
          payment_method: form.paymentMethod,
        },
      });
      const payload = {
        customer: {
          customer_id: customer?.id || null,
          email: customer?.email || null,
          full_name: form.fullName.trim(),
          phone: form.phone.trim(),
          address: form.address.trim() || null,
          city: form.city.trim() || null,
          notes: form.notes.trim() || null,
        },
        payment_method: form.paymentMethod,
        language: lang,
        items: detailedItems.map((i) => ({
          product_id: i.id,
          name: localized(i.product.name, lang),
          size: i.size || null,
          // `color` carries the canonical EN identifier so the backend can
          // resolve the `size::color` variant key in the stock map. The
          // localized label travels separately for emails / receipts.
          color: i.color || null,
          color_display: i.color
            ? localizedColor(i.product, i.color, lang)
            : (i.product.color ? localized(i.product.color, lang) : null),
          authentic: !!i.product.authentic,
          quantity: i.qty,
          unit_price: i.unitPrice,
          original_price: i.product.originalPrice,
          line_total: i.lineTotal,
          image_url: resolveAssetUrl(i.product.images?.[0]) || null,
        })),
        subtotal,
        shipping_fee: shippingFee,
        total,
      };

      const res = await fetch(`${BACKEND_URL}/api/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // 409 = stock conflict (out-of-stock or oversold race). The backend
        // now returns a STRUCTURED detail listing every unavailable line so
        // we can highlight exactly which cart items the customer must remove
        // — rather than dumping the entire cart.
        if (res.status === 409) {
          refreshStock().catch(() => {});
          const detail = data?.detail;
          const items = Array.isArray(detail) ? detail : (detail?.items || []);
          if (Array.isArray(items) && items.length > 0) {
            const keys = new Set(
              items.map((u) => matchKey(u.product_id, u.size || "", u.color || ""))
            );
            setUnavailableKeys(keys);
            throw new Error(S.unavailableItemsMessage);
          }
          throw new Error(typeof detail === "string" ? detail : S.unavailableItemsMessage);
        }
        throw new Error(data?.detail || "Order failed");
      }

      // Refresh the live stock map so any open product pages/cards update
      // immediately (in case this order just consumed the last unit).
      refreshStock().catch(() => {});

      // Order is persisted server-side. If the confirmation email did not go
      // out (e.g. transactional provider misconfigured), surface a clear note
      // rather than silently confirm — per the "no silent failure" rule.
      const emailOk = data.email_sent !== false;
      trackEvent("checkout_success", {
        routeName: "checkout_modal",
        metadata: {
          order_number: data.order_number,
          items_count: detailedItems.length,
          total,
          payment_method: form.paymentMethod,
          email_sent: emailOk,
        },
      });
      setSuccess({ order_no: data.order_number, email_sent: emailOk });
      clearCart();
    } catch (err) {
      // Surface the backend detail when it's a stock error so the customer
      // knows exactly which item to remove. The unavailable-line keys are
      // already set above so the order summary renders them with badges.
      const msg = err?.message || "";
      if (unavailableKeys.size > 0 || msg === S.unavailableItemsMessage) {
        setSubmitError(S.unavailableItemsMessage);
      } else if (
        msg.toLowerCase().includes("out of stock") ||
        msg.toLowerCase().includes("only ")
      ) {
        setSubmitError(msg);
      } else {
        setSubmitError(C.orderError);
      }
      trackEvent("checkout_failed", {
        routeName: "checkout_modal",
        metadata: {
          items_count: detailedItems.length,
          total,
          payment_method: form.paymentMethod,
          reason:
            unavailableKeys.size > 0 || msg === S.unavailableItemsMessage
              ? "stock_conflict"
              : msg.toLowerCase().includes("out of stock") || msg.toLowerCase().includes("only ")
              ? "stock_conflict"
              : "request_failed",
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  // One-click action: drop every line the backend flagged as unavailable.
  const handleRemoveUnavailable = () => {
    const keys = Array.from(unavailableKeys);
    keys.forEach((k) => removeFromCart(k));
    setUnavailableKeys(new Set());
    setSubmitError("");
  };

  const handleWhatsAppHelp = () => {
    const helpMsg =
      lang === "ar"
        ? "مرحباً BOB Services LB، أحتاج مساعدة في إتمام طلبي عبر المتجر."
        : lang === "fr"
        ? "Bonjour BOB Services LB, j'ai besoin d'aide pour finaliser ma commande."
        : "Hi BOB Services LB, I need help completing my order from the store.";
    trackEvent("whatsapp_click", {
      routeName: "checkout_modal",
      metadata: { source: "checkout_help" },
    });
    trackEvent("contact_click", {
      routeName: "checkout_modal",
      metadata: { source: "checkout_help", channel: "whatsapp" },
    });
    window.open(buildWhatsAppUrl(helpMsg), "_blank", "noopener,noreferrer");
  };

  const handleCloseAfterSuccess = () => {
    setSuccess(null);
    setForm({ fullName: "", phone: "", address: "", city: "", notes: "", paymentMethod: "omt" });
    onClose();
    closeCart();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto overscroll-contain"
      onClick={onClose}
      data-testid="checkout-modal"
      role="dialog"
      aria-modal="true"
      aria-label={C.title}
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div
        className="relative bg-white w-full sm:max-w-5xl my-0 sm:my-6 sm:rounded-[24px] shadow-2xl sm:overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 end-4 w-10 h-10 rounded-full bg-white border border-black/10 hover:border-[#111] flex items-center justify-center z-10"
          data-testid="checkout-close"
          aria-label={C.close}
        >
          <X className="w-5 h-5 text-[#111]" />
        </button>

        {success ? (
          <SuccessPanel
            orderNo={success.order_no}
            message={C.orderSuccessMsg.replace("{order_no}", success.order_no)}
            successTitle={C.orderSuccess}
            backLabel={C.backToShopping}
            onClose={handleCloseAfterSuccess}
          />
        ) : (
          <div className={`grid lg:grid-cols-[1.05fr_0.95fr] ${isRTL ? "rtl" : ""}`}>
            {/* LEFT: Logo + Form + Payment (shown FIRST on mobile so user fills fields immediately) */}
            <form
              onSubmit={handleSubmit}
              className="bg-white p-6 sm:p-9 lg:p-10 order-1"
              data-testid="checkout-form"
              noValidate
            >
              <img src={LOGO_URL} alt="BOB Services LB" className="h-14 w-auto mb-5" />

              <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#111]">{C.title}</h2>
              <p className="text-sm text-[#666] mt-1.5">{C.contactInfo}</p>

              {/* Customer info */}
              <div className="mt-5 space-y-4">
                <Field
                  label={`${C.fullName} *`}
                  testId="checkout-fullname"
                  value={form.fullName}
                  onChange={handleChange("fullName")}
                  error={errors.fullName}
                  required
                />
                <Field
                  label={`${C.phone} *`}
                  testId="checkout-phone"
                  value={form.phone}
                  onChange={handleChange("phone")}
                  error={errors.phone}
                  type="tel"
                  inputMode="tel"
                  required
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field
                    label={C.address}
                    testId="checkout-address"
                    value={form.address}
                    onChange={handleChange("address")}
                  />
                  <Field
                    label={C.city}
                    testId="checkout-city"
                    value={form.city}
                    onChange={handleChange("city")}
                  />
                </div>
                <Field
                  label={C.notes}
                  testId="checkout-notes"
                  value={form.notes}
                  onChange={handleChange("notes")}
                  placeholder={C.notesPlaceholder}
                  as="textarea"
                />
              </div>

              {/* Payment methods */}
              <h3 className="mt-7 mb-3 text-sm font-semibold tracking-[0.2em] uppercase text-[#F37021]">
                {C.paymentMethods}
              </h3>
              <div className="grid sm:grid-cols-3 gap-3">
                {paymentMethods.map((m) => {
                  const active = form.paymentMethod === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, paymentMethod: m.id }))}
                      className={`relative text-start p-3.5 rounded-2xl border transition-colors ${
                        active
                          ? "border-[#F37021] bg-[#FFF3EA] ring-2 ring-[#F37021]/30"
                          : "border-black/10 bg-white hover:border-[#F37021]"
                      }`}
                      data-testid={`checkout-pay-${m.id}`}
                    >
                      <div className="text-sm font-semibold text-[#111]">{C[m.labelKey]}</div>
                      <div className="text-[11px] text-[#777] mt-0.5">{C[m.subKey]}</div>
                      {active && (
                        <span className="absolute top-2 end-2 w-5 h-5 rounded-full bg-[#F37021] text-white flex items-center justify-center">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Submit + Error */}
              <button
                type="submit"
                disabled={submitting || detailedItems.length === 0}
                className="mt-7 w-full h-14 rounded-full bg-[#F37021] hover:bg-[#E06015] disabled:bg-[#CCC] disabled:cursor-not-allowed text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors shadow-[0_14px_30px_rgba(243,112,33,0.25)]"
                data-testid="checkout-submit"
              >
                {submitting ? C.placing : C.placeOrder}
              </button>

              {submitError && (
                <div
                  className="mt-3 p-3 rounded-2xl bg-red-50 border border-red-200 text-sm text-red-700"
                  data-testid="checkout-error"
                >
                  <div className="inline-flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{submitError}</span>
                  </div>
                  {unavailableKeys.size > 0 && (
                    <button
                      type="button"
                      onClick={handleRemoveUnavailable}
                      className="mt-2 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-semibold transition-colors"
                      data-testid="checkout-remove-unavailable"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {S.removeUnavailableCta}
                    </button>
                  )}
                </div>
              )}

              {/* WhatsApp help fallback */}
              <button
                type="button"
                onClick={handleWhatsAppHelp}
                className="mt-4 w-full text-center text-sm text-[#25D366] hover:text-[#1EBE5C] font-medium inline-flex items-center justify-center gap-1.5"
                data-testid="checkout-whatsapp-help"
              >
                <MessageCircle className="w-4 h-4" />
                {S.helpOnWhatsapp}
              </button>
            </form>

            {/* RIGHT: Order summary (shown SECOND on mobile so form is reachable; first on desktop's right column) */}
            <aside
              className="bg-[#FAFAFA] border-t lg:border-t-0 lg:border-s border-black/5 p-6 sm:p-9 lg:p-10 order-2 lg:order-2"
              data-testid="checkout-summary"
            >
              <div className="flex items-center justify-between gap-3 mb-5">
                <h3 className="font-display text-lg sm:text-xl font-bold text-[#111]">
                  {C.yourOrder}
                </h3>
                <span className="text-xs font-medium text-[#666]">
                  {itemsCount} {itemsCount === 1 ? C.item : C.items}
                </span>
              </div>

              <ul className="space-y-3 lg:max-h-[55vh] lg:overflow-y-auto pe-1">
                {detailedItems.map((i) => {
                  const name = localized(i.product.name, lang);
                  const color = i.color
                    ? localizedColor(i.product, i.color, lang)
                    : (i.product.color ? localized(i.product.color, lang) : "");
                  const isUnavailable = unavailableKeys.has(i.key);
                  return (
                    <li
                      key={i.key}
                      className={`flex gap-3 p-2.5 rounded-2xl border ${
                        isUnavailable
                          ? "bg-red-50 border-red-200"
                          : "bg-white border-black/5"
                      }`}
                      data-testid={`checkout-line-${i.id}`}
                    >
                      <div className="shrink-0 w-16 h-20 rounded-xl overflow-hidden bg-[#0F0F0F] relative">
                        <img src={resolveAssetUrl(i.product.images?.[0])} alt={name} className={`w-full h-full object-cover ${isUnavailable ? "opacity-40 grayscale" : ""}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#F37021]">#{i.id}</span>
                          {i.product.authentic && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#7A4F00]">
                              <ShieldCheck className="w-3 h-3" />
                              {S.badgeAuthentic}
                            </span>
                          )}
                          {isUnavailable && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-700 bg-red-100 px-2 py-0.5 rounded-full"
                              data-testid={`checkout-line-unavailable-${i.id}`}
                            >
                              <AlertCircle className="w-3 h-3" />
                              {S.unavailableItemsBadge}
                            </span>
                          )}
                        </div>
                        <div className="font-display text-sm font-semibold text-[#111] leading-tight line-clamp-2">
                          {name}
                        </div>
                        {(i.size || color) && (
                          <div className="mt-0.5 text-[11px] text-[#666] flex flex-wrap gap-1.5">
                            {i.size && <span>{S.sizeLabel}: <span className="text-[#111] font-semibold">{i.size}</span></span>}
                            {color && <span>•&nbsp;{color}</span>}
                          </div>
                        )}
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <span className="text-xs text-[#666]">
                            {C.qtyLabel} {i.qty} × {formatPrice(i.unitPrice)}
                          </span>
                          {isUnavailable ? (
                            <button
                              type="button"
                              onClick={() => removeFromCart(i.key)}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700"
                              data-testid={`checkout-line-remove-${i.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              {S.removeUnavailableCta}
                            </button>
                          ) : (
                            <span className="text-sm font-bold text-[#111]">{formatPrice(i.lineTotal)}</span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-5 pt-4 border-t border-black/5 space-y-2">
                <Row label={S.subtotal} value={formatPrice(subtotal)} />
                <Row label={S.shippingLebanon} value={formatPrice(shippingFee)} />
                <Row label={S.total} value={formatPrice(total)} bold />
              </div>

              <PolicyCard variant="compact" className="mt-4" />
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, testId, type = "text", error, placeholder, as, required, inputMode }) => {
  const Cmp = as === "textarea" ? "textarea" : "input";
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#555] mb-1.5">
        {label}
      </span>
      <Cmp
        type={as === "textarea" ? undefined : type}
        inputMode={inputMode}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={as === "textarea" ? 3 : undefined}
        required={required}
        className={`w-full px-4 py-3 rounded-2xl bg-white border text-sm text-[#111] placeholder:text-[#999] focus:outline-none transition-colors ${
          error
            ? "border-red-400 focus:border-red-500"
            : "border-black/10 focus:border-[#F37021]"
        }`}
        data-testid={testId}
      />
      {error && (
        <span className="block mt-1 text-xs text-red-600" data-testid={`${testId}-error`}>
          {error}
        </span>
      )}
    </label>
  );
};

const Row = ({ label, value, bold }) => (
  <div className="flex items-center justify-between">
    <span className={`${bold ? "text-base font-semibold text-[#111]" : "text-sm text-[#666]"}`}>{label}</span>
    <span className={`${bold ? "font-display text-xl font-bold text-[#F37021]" : "text-sm font-semibold text-[#111]"}`}>
      {value}
    </span>
  </div>
);

const SuccessPanel = ({ orderNo, message, successTitle, backLabel, onClose }) => (
  <div className="p-10 text-center" data-testid="checkout-success">
    <div className="w-20 h-20 rounded-full bg-[#E9F8EE] flex items-center justify-center mx-auto mb-5">
      <CheckCircle2 className="w-10 h-10 text-[#1EBE5C]" />
    </div>
    <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#111]">{successTitle}</h3>
    <p className="mt-3 text-[#555] max-w-md mx-auto">{message}</p>
    <div className="mt-3 inline-block px-4 py-2 rounded-full bg-[#FFF3EA] text-[#F37021] text-xs font-bold tracking-[0.25em] uppercase">
      #{orderNo}
    </div>
    <button
      type="button"
      onClick={onClose}
      className="mt-7 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#111] hover:bg-[#F37021] text-white font-medium transition-colors"
      data-testid="checkout-success-close"
    >
      {backLabel}
    </button>
  </div>
);
